import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Flame, Lock, Search, Zap } from "lucide-react";
import { Badge } from "@/components/Badge";
import { Badge as BadgeUi } from "@/components/ui/badge";
import { EmptyState } from "@/components/EmptyState";
import { ErrorState } from "@/components/ErrorState";
import { STAGE_COLORS } from "@/lib/constants";
import { formatDate, formatDateTime } from "@/lib/dates";
import { ApiError } from "@/api/client";
import { getPraticaRete, isAccessoSospeso } from "@/api/rete";
import type { DettaglioPraticaAgenzia } from "@/types";
import { AccessoSospeso } from "./AccessoSospeso";

function euro(n: number): string {
  return `€${n.toLocaleString("it-IT", { minimumFractionDigits: 2 })}`;
}

function Field({ label, value }: { label: string; value: string | null | undefined }) {
  return (
    <div className="break-words">
      <span className="text-muted-foreground">{label}:</span>{" "}
      <span className="text-text">{value ?? "—"}</span>
    </div>
  );
}

const TORNA = (
  <Link to="/rete/pratiche" className="inline-flex items-center gap-2 min-h-[44px] text-sm text-muted-foreground hover:text-text">
    <ArrowLeft size={16} /> Torna alle pratiche
  </Link>
);

/**
 * Dettaglio in sola lettura: il responsabile guarda la pratica, non la lavora.
 * Nessun bottone d'azione, nessuno scarico (bollette e documento restano all'agenzia).
 */
export function DettaglioPraticaRete() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DettaglioPraticaAgenzia | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sospeso, setSospeso] = useState(false);

  useEffect(() => {
    if (!id) return;
    let cancelled = false;
    setLoading(true);
    setNotFound(false);
    setError(null);
    getPraticaRete(id)
      .then((d) => { if (!cancelled) setData(d); })
      .catch((e) => {
        if (cancelled) return;
        if (isAccessoSospeso(e)) setSospeso(true);
        else if (e instanceof ApiError && e.status === 404) setNotFound(true);
        else setError("Impossibile caricare la pratica.");
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [id]);

  if (sospeso) return <AccessoSospeso />;
  if (loading) return <div className="p-6 text-sm text-muted-foreground">Caricamento...</div>;
  if (notFound) {
    return (
      <div className="space-y-4">
        {TORNA}
        <EmptyState icon={<Search size={26} className="text-brand" />} title="Pratica non trovata" />
      </div>
    );
  }
  if (error) return <ErrorState message={error} />;
  if (!data) return null;

  const stageColor = STAGE_COLORS[data.stage] ?? STAGE_COLORS["Bozza"];

  return (
    <div className="space-y-4">
      {TORNA}

      <div className="flex flex-wrap items-center gap-3">
        <h2 className="text-lg font-semibold text-text">{data.name}</h2>
        <Badge bg={stageColor.bg} text={stageColor.text}>{data.stage}</Badge>
        <BadgeUi variant="outline" className="gap-1 text-muted-foreground">
          <Lock size={12} aria-hidden="true" /> Sola lettura
        </BadgeUi>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Dati pratica</h3>
          <div className="space-y-2 text-sm">
            <Field label="Agenzia" value={data.account_name} />
            <Field label="Stato" value={data.stage} />
            <Field label="Tipo" value={data.tipo} />
            <Field label="Creata il" value={formatDateTime(data.created_at)} />
            <Field label="Data chiusura" value={formatDate(data.close_date)} />
            <Field label="Quota agenzia" value={data.quota_agenzia != null ? euro(data.quota_agenzia) : null} />
            <Field label="POD" value={data.pod} />
            <Field label="PDR" value={data.pdr} />
          </div>
        </div>

        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Cliente</h3>
          <div className="space-y-2 text-sm">
            <div className="break-words">
              <span className="text-muted-foreground">Nome:</span>{" "}
              {data.cliente_id ? (
                <Link to={`/rete/clienti/${encodeURIComponent(data.cliente_id)}`} className="text-brand hover:underline">
                  {data.cliente_name ?? "—"}
                </Link>
              ) : (
                <span className="text-text">{data.cliente_name ?? "—"}</span>
              )}
            </div>
            <Field label="Codice fiscale" value={data.codice_fiscale} />
            <Field label="IBAN" value={data.iban} />
          </div>
        </div>
      </div>

      {data.offerta_scelta && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Offerta scelta</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2 text-sm">
            <Field label="Offerta" value={data.offerta_scelta.nome} />
            <Field label="Fornitore" value={data.offerta_scelta.fornitore} />
            <Field label="Tipo" value={data.offerta_scelta.tipo} />
            <Field label="Prezzo" value={data.offerta_scelta.prezzo} />
            <Field label="Quota fissa" value={`€${data.offerta_scelta.quota_fissa_mese.toFixed(2)}/mese`} />
            <Field label="Durata" value={`${data.offerta_scelta.durata_mesi} mesi`} />
            <Field label="Fatturazione" value={data.offerta_scelta.fatturazione} />
          </div>
        </div>
      )}

      {data.bollette && data.bollette.length > 0 && (
        <div className="bg-card p-4 rounded-lg border border-border">
          <h3 className="font-semibold text-text mb-3">Bollette</h3>
          {/* Solo l'elenco: dalla rete le bollette non si aprono né si scaricano. */}
          <div className="space-y-2">
            {data.bollette.map((b) => (
              <div key={b.id} className="flex items-center gap-3 rounded-lg border border-border px-3 py-2">
                <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg bg-brand/10">
                  {b.tipoFornitura === "Gas"
                    ? <Flame className="h-4 w-4 text-orange-500" />
                    : <Zap className="h-4 w-4 text-amber-500" />}
                </span>
                <div className="flex-1 min-w-0">
                  <div className="truncate font-medium text-text" title={b.name}>{b.name}</div>
                  <div className="text-xs text-muted-foreground truncate">
                    {[
                      b.tipoFornitura,
                      b.fornitoreAttuale,
                      b.dataBolletta ? formatDate(b.dataBolletta) : null,
                      b.importoMensile ? `€ ${b.importoMensile.toFixed(2)}/mese` : null,
                    ].filter(Boolean).join(" · ") || "—"}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
